/**
 * Trial signup, signup survey and trial settings (`app/services/signup_service.py`).
 *
 * Split out of `brainfocus_api.ts` (FE Arch 1.0, #1165), which re-exports everything
 * here through the `BrainFocusApi` facade. Requests go through `client.request` so tests
 * can stub it on the module namespace.
 */
import * as client from '@brainfocus/web/api/client';
import { SurveyQuestion, SurveyResult } from '@brainfocus/web/domain/survey/survey_format';
import { assertPojo } from '@brainfocus/web/api/response';
import type { NotificationEmail } from '@brainfocus/web/api/types';

/**
 * Submit signup request
 */
export const signup = client.endpointJson<SignupPayload, SignupResult>('/api/signup');

/**
 * Load current survey accompanying signup
 */
export const getSurvey = client.endpointJson<void, SurveyQuestion>('/api/survey');

/**
 * Get trial signup notification email recipients (SuperAdmin only)
 */
export async function getTrialNotificationEmails(): Promise<NotificationEmail[]> {
    const response = await client.request('POST', '/admin/get-trial-notification-emails', {});
    assertPojo<{ emails: NotificationEmail[] }>(response);
    return (response as { emails: NotificationEmail[] }).emails;
}

/**
 * Add a trial signup notification email recipient (SuperAdmin only)
 */
export async function addTrialNotificationEmail(email: string): Promise<NotificationEmail> {
    const response = await client.request('POST', '/admin/add-trial-notification-email', { email });
    assertPojo<NotificationEmail>(response);
    return response as NotificationEmail;
}

/**
 * Delete a trial signup notification email recipient (SuperAdmin only)
 */
export async function deleteTrialNotificationEmail(id: number): Promise<void> {
    await client.request('POST', '/admin/delete-trial-notification-email', { id });
}

/**
 * Get trial signup settings (SuperAdmin only)
 */
export async function getTrialSettings(): Promise<TrialSettings> {
    const response = await client.request('POST', '/admin/get-trial-settings', {});
    assertPojo<TrialSettings>(response);
    return response as TrialSettings;
}

/**
 * Update trial signup settings (SuperAdmin only)
 */
export async function updateTrialSettings(settings: UpdateTrialSettingsPayload): Promise<void> {
    await client.request('POST', '/admin/update-trial-settings', settings);
}

export interface SignupPayload {
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
    institution: string;
    position: string;
    country: string;
    password: string;
    passwordConfirm: string;
    tocAcceptedAt: string;
    truthfulnessConfirmedAt: string;
    fieldOfWorkOther?: string;
    survey: SurveyResult;
}

export interface SignupResult {
    accountReady: boolean;
    /** True for industry players, whose access waits on NDA verification (#869). */
    ndaPending?: boolean;
}

/**
 * Trial signup settings (SuperAdmin only)
 */
export interface TrialSettings {
    trialSignupEnabled: boolean;
    disabledMessage: string;
}

/**
 * Payload for updating trial signup settings
 */
export interface UpdateTrialSettingsPayload {
    trialSignupEnabled: boolean;
    disabledMessage?: string;
}
